import { useState, useEffect, useRef } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import EmojiPicker, { Theme } from "emoji-picker-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  X,
  Check,
  ChevronRight,
  ChevronDown,
  Users,
  Calendar as CalendarIcon,
  Tag,
  Plus,
  Clock,
  Bell,
  Repeat,
  Send,
  AtSign,
  Smile,
  Search,
  Edit2,
  MoreHorizontal,
  ArrowRight,
  Zap,
  Trash2,
  Sun,
  Link2,
  Timer,
  LayoutGrid,
  Archive,
  Paperclip,
  CheckSquare,
  Download,
  FileText,
  Image,
  Film,
  File,
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

// Predefined tags with colors
const defaultTags = [
  { id: "urgent-important", name: "Urgent and Important", color: "#e74c3c" },
  { id: "urgent-not-important", name: "Urgent Not Important", color: "#e67e22" },
  { id: "not-urgent-important", name: "Not Urgent & Important", color: "#3498db" },
  { id: "not-urgent-not-important", name: "Not Urgent and Not Important", color: "#27ae60" },
  { id: "work-completed", name: "Work Completed", color: "#9b59b6" },
  { id: "in-process", name: "In Process", color: "#1abc9c" },
];

const mergeTagLists = (defaults, custom) => {
  const ids = new Set(defaults.map(t => t.id));
  const merged = [...defaults];
  for (const t of custom) {
    if (!ids.has(t.tag_id)) {
      merged.push({ id: t.tag_id, name: t.name, color: t.color });
    }
  }
  return merged;
};

// Boards/Sections
const boards = [
  { id: "urgent-important", name: "Task : Urgent & Important" },
  { id: "followups", name: "Task : Followups with team" },
  { id: "important-regular", name: "Task : Important & Regular" },
  { id: "event-followups", name: "Task : Event Followups" },
  { id: "done", name: "Done" },
];

// Time options
const timeOptions = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hour = h.toString().padStart(2, '0');
    const min = m.toString().padStart(2, '0');
    timeOptions.push(`${hour}:${min}`);
  }
}

// Recurring frequencies
const recurringFrequencies = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function TaskDetailPanel({ task, lists, onClose, onUpdate, onDelete, currentUser, activeGroupId }) {
  const [title, setTitle] = useState(task?.title || "");
  const [description, setDescription] = useState(task?.description || "");
  const [dueDate, setDueDate] = useState(task?.due_date ? new Date(task.due_date) : null);
  const [dueTime, setDueTime] = useState(task?.due_time || "");
  const [startDate, setStartDate] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [assignees, setAssignees] = useState(task?.assignees || []);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState(task?.comments || []);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentBoard, setCurrentBoard] = useState(task?.priority === "high" ? "urgent-important" : "important-regular");
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [mobileMoreMenuOpen, setMobileMoreMenuOpen] = useState(false);
  
  // Team members state - fetched from API
  const [members, setMembers] = useState([]);

  // Custom tags from API
  const [customTags, setCustomTags] = useState([]);
  const allTags = mergeTagLists(defaultTags, customTags);
  
  // Reminders
  const [reminders, setReminders] = useState(task?.reminders || []);
  const [reminderDate, setReminderDate] = useState(null);
  const [reminderTime, setReminderTime] = useState("09:00");
  
  // Recurring
  const [recurringEnabled, setRecurringEnabled] = useState(task?.recurring?.enabled || false);
  const [recurringFrequency, setRecurringFrequency] = useState(task?.recurring?.frequency || "daily");
  const [recurringInterval, setRecurringInterval] = useState(task?.recurring?.interval || 1);
  const [recurringDays, setRecurringDays] = useState(task?.recurring?.days_of_week || []);
  const [recurringEndDate, setRecurringEndDate] = useState(null);
  
  // Popover states
  const [assignOpen, setAssignOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [newTagOpen, setNewTagOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#e74c3c");
  const [dateTab, setDateTab] = useState("due");
  
  // Attachment & Checklist state
  const [attachments, setAttachments] = useState(task?.attachments?.filter(a => !a.is_deleted) || []);
  const [checklist, setChecklist] = useState(task?.checklist || []);
  const [newChecklistItem, setNewChecklistItem] = useState("");
  const [showChecklistInput, setShowChecklistInput] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  
  const commentInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-refresh comments by polling the task every 5 seconds
  useEffect(() => {
    if (!task?.task_id) return;
    const pollComments = async () => {
      try {
        const res = await fetch(`${API}/tasks/${task.task_id}`, { credentials: "include" });
        if (res.ok) {
          const updated = await res.json();
          if (updated.comments && updated.comments.length !== comments.length) {
            setComments(updated.comments);
          }
        }
      } catch { /* silent */ }
    };
    const interval = setInterval(pollComments, 5000);
    return () => clearInterval(interval);
  }, [task?.task_id, comments.length]);

  useEffect(() => {
    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      setDueDate(task.due_date ? new Date(task.due_date) : null);
      setDueTime(task.due_time || "");
      setComments(task.comments || []);
      setReminders(task.reminders || []);
      setAssignees(task.assignees || []);
      setAttachments((task.attachments || []).filter(a => !a.is_deleted));
      setChecklist(task.checklist || []);
      if (task.recurring) {
        setRecurringEnabled(task.recurring.enabled || false);
        setRecurringFrequency(task.recurring.frequency || "daily");
        setRecurringInterval(task.recurring.interval || 1);
        setRecurringDays(task.recurring.days_of_week || []);
      }
      // Restore tags from saved tag IDs
      if (task.tags && task.tags.length > 0) {
        const restored = task.tags.map(tagId => {
          const found = allTags.find(t => t.id === tagId);
          return found || { id: tagId, name: tagId, color: "#6b7280" };
        });
        setSelectedTags(restored);
      } else {
        setSelectedTags([]);
      }
      // Set current board
      setCurrentBoard(task.status || (task.priority === "high" ? "urgent-important" : "important-regular"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, customTags]);

  // Fetch team members and custom tags from API
  const groupId = task?.group_id || activeGroupId;
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        // If task belongs to a group, fetch group members; otherwise fetch personal team members
        const url = groupId
          ? `${API}/groups/${groupId}/members`
          : `${API}/team/members`;
        const res = await fetch(url, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setMembers(data);
        }
      } catch (err) {
        console.error("Failed to fetch members:", err);
      }
    };
    const fetchTags = async () => {
      try {
        const res = await fetch(`${API}/tags`, { credentials: "include" });
        if (res.ok) setCustomTags(await res.json());
      } catch (err) {
        console.error("Failed to fetch tags:", err);
      }
    };
    fetchMembers();
    fetchTags();
  }, [groupId]);

  const handleSave = async (showToast = false) => {
    if (!task) return;
    
    // Skip save if nothing changed
    const titleChanged = title !== (task.title || "");
    const descChanged = (description || "") !== (task.description || "");
    if (!showToast && !titleChanged && !descChanged) return;
    
    try {
      const payload = {
        title: title || task.title,
        description: description || null,
        due_date: dueDate ? dueDate.toISOString() : null,
        due_time: dueTime || null,
        reminders,
        recurring: recurringEnabled ? {
          enabled: true,
          frequency: recurringFrequency,
          interval: recurringInterval,
          days_of_week: recurringDays,
          end_date: recurringEndDate ? recurringEndDate.toISOString() : null,
        } : null,
      };

      const response = await fetch(`${API}/tasks/${task.task_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const updatedTask = await response.json();
        onUpdate(updatedTask);
        if (showToast) toast.success("Task updated");
      }
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const handleMarkComplete = async () => {
    if (!task) return;
    
    try {
      const response = await fetch(`${API}/tasks/${task.task_id}/toggle`, {
        method: "PATCH",
        credentials: "include",
      });

      if (response.ok) {
        const updatedTask = await response.json();
        onUpdate(updatedTask);
        toast.success(updatedTask.completed ? "Task completed!" : "Task reopened");
        if (updatedTask.completed) onClose();
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleBoardChange = async (boardId) => {
    if (!task) return;
    
    let newPriority = task.priority;
    if (boardId === "urgent-important") newPriority = "high";
    else if (boardId === "followups" || boardId === "important-regular") newPriority = "medium";
    else newPriority = "low";

    try {
      const response = await fetch(`${API}/tasks/${task.task_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...task, priority: newPriority }),
      });

      if (response.ok) {
        const updatedTask = await response.json();
        onUpdate(updatedTask);
        setCurrentBoard(boardId);
        setSectionOpen(false);
        toast.success("Task moved!");
      }
    } catch (error) {
      toast.error("Failed to move task");
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !task) return;

    try {
      const response = await fetch(`${API}/tasks/${task.task_id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: comment.trim() }),
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setComments(updatedTask.comments || []);
        onUpdate(updatedTask);
        setComment("");
        setShowEmojiPicker(false);
        toast.success("Comment added");
      }
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!task) return;

    try {
      const response = await fetch(`${API}/tasks/${task.task_id}/comments/${commentId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setComments(prev => prev.filter(c => c.id !== commentId));
        toast.success("Comment deleted");
      }
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleAddReminder = () => {
    if (!reminderDate) {
      toast.error("Please select a date");
      return;
    }
    
    const reminderDateTime = new Date(reminderDate);
    const [hours, mins] = reminderTime.split(':');
    reminderDateTime.setHours(parseInt(hours), parseInt(mins));
    
    const newReminder = {
      id: `rem_${Date.now()}`,
      datetime: reminderDateTime.toISOString(),
      type: "notification",
    };
    
    setReminders(prev => [...prev, newReminder]);
    setReminderDate(null);
    setReminderOpen(false);
    toast.success("Reminder added");
    handleSave(true);
  };

  const handleRemoveReminder = (id) => {
    setReminders(prev => prev.filter(r => r.id !== id));
    handleSave(true);
  };

  const toggleTag = async (tag) => {
    const exists = selectedTags.find(t => t.id === tag.id);
    const newTags = exists 
      ? selectedTags.filter(t => t.id !== tag.id) 
      : [...selectedTags, tag];
    
    setSelectedTags(newTags);
    
    // Save tag IDs to task backend
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ tags: newTags.map(t => t.id) }),
      });
      if (res.ok) {
        const updatedTask = await res.json();
        onUpdate(updatedTask);
      }
    } catch (err) {
      console.error("Failed to save tags:", err);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const res = await fetch(`${API}/tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newTagName.trim(), color: newTagColor }),
      });
      if (res.ok) {
        const created = await res.json();
        setCustomTags(prev => [...prev, created]);
        // Auto-select the new tag
        const newTag = { id: created.tag_id, name: created.name, color: created.color };
        const newTags = [...selectedTags, newTag];
        setSelectedTags(newTags);
        setNewTagName("");
        setNewTagOpen(false);
        toast.success("Tag created");
        // Save to task
        const taskRes = await fetch(`${API}/tasks/${task.task_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ tags: newTags.map(t => t.id) }),
        });
        if (taskRes.ok) {
          const updatedTask = await taskRes.json();
          onUpdate(updatedTask);
        }
      }
    } catch {
      toast.error("Failed to create tag");
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      const res = await fetch(`${API}/tags/${tagId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setCustomTags(prev => prev.filter(t => t.tag_id !== tagId));
        const newTags = selectedTags.filter(t => t.id !== tagId);
        setSelectedTags(newTags);
        // Update task tags too
        const taskRes = await fetch(`${API}/tasks/${task.task_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ tags: newTags.map(t => t.id) }),
        });
        if (taskRes.ok) {
          const updatedTask = await taskRes.json();
          onUpdate(updatedTask);
        }
        toast.success("Tag deleted");
      }
    } catch {
      toast.error("Failed to delete tag");
    }
  };

  const handleToggleAssignee = async (memberId) => {
    const newAssignees = assignees.includes(memberId)
      ? assignees.filter(id => id !== memberId)
      : [...assignees, memberId];
    
    setAssignees(newAssignees);
    
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/assignees`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ assignees: newAssignees }),
      });
      if (res.ok) {
        const updatedTask = await res.json();
        onUpdate(updatedTask);
      }
    } catch (error) {
      toast.error("Failed to update assignees");
      setAssignees(assignees); // revert
    }
  };

  const handleChangeMemberRole = async (memberId, newRole) => {
    try {
      const res = await fetch(`${API}/team/members/${memberId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        const updated = await res.json();
        setMembers(prev => prev.map(m => m.member_id === memberId ? updated : m));
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      const res = await fetch(`${API}/team/members/${memberId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setMembers(prev => prev.filter(m => m.member_id !== memberId));
        // Also remove from assignees if assigned
        if (assignees.includes(memberId)) {
          const newAssignees = assignees.filter(id => id !== memberId);
          setAssignees(newAssignees);
        }
        toast.success("Member removed");
      }
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const toggleRecurringDay = (dayIndex) => {
    setRecurringDays(prev => {
      if (prev.includes(dayIndex)) {
        return prev.filter(d => d !== dayIndex);
      }
      return [...prev, dayIndex];
    });
  };

  const handleAddToMyDay = async () => {
    if (!task) return;
    const today = new Date().toISOString().split("T")[0];
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ my_day_date: today }),
      });
      if (res.ok) {
        const updated = await res.json();
        onUpdate(updated);
        toast.success("Added to My Day");
      }
    } catch {
      toast.error("Failed to add to My Day");
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/task/${task.task_id}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const handleArchiveTask = async () => {
    if (!task) return;
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ archived: true }),
      });
      if (res.ok) {
        const updated = await res.json();
        onUpdate(updated);
        toast.success("Task archived");
        onClose();
      }
    } catch {
      toast.error("Failed to archive task");
    }
  };

  // ===== Attachment Handlers =====
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !task) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File too large. Max 10MB.");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${API}/tasks/${task.task_id}/attachments`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (res.ok) {
        const updated = await res.json();
        setAttachments((updated.attachments || []).filter(a => !a.is_deleted));
        onUpdate(updated);
        toast.success("File attached");
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!task) return;
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/attachments/${attachmentId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const updated = await res.json();
        setAttachments((updated.attachments || []).filter(a => !a.is_deleted));
        onUpdate(updated);
        toast.success("Attachment removed");
      }
    } catch {
      toast.error("Failed to remove attachment");
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/attachments/${attachment.id}/download`, {
        credentials: "include",
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error("Download failed");
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith("image/")) return Image;
    if (contentType?.startsWith("video/")) return Film;
    return FileText;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ===== Checklist Handlers =====
  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim() || !task) return;
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/checklist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: newChecklistItem.trim() }),
      });
      if (res.ok) {
        const updated = await res.json();
        setChecklist(updated.checklist || []);
        onUpdate(updated);
        setNewChecklistItem("");
      }
    } catch {
      toast.error("Failed to add item");
    }
  };

  const handleToggleChecklistItem = async (itemId) => {
    if (!task) return;
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/checklist/${itemId}/toggle`, {
        method: "PATCH",
        credentials: "include",
      });
      if (res.ok) {
        const updated = await res.json();
        setChecklist(updated.checklist || []);
        onUpdate(updated);
      }
    } catch {
      toast.error("Failed to update item");
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    if (!task) return;
    try {
      const res = await fetch(`${API}/tasks/${task.task_id}/checklist/${itemId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const updated = await res.json();
        setChecklist(updated.checklist || []);
        onUpdate(updated);
      }
    } catch {
      toast.error("Failed to delete item");
    }
  };

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const assignedMembers = members.filter(m => assignees.includes(m.member_id));
  const unassignedMembers = filteredMembers.filter(m => !assignees.includes(m.member_id));

  const filteredTags = allTags.filter(t =>
    t.name.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const tagColors = [
    "#e74c3c", "#e67e22", "#f1c40f", "#2ecc71", "#1abc9c", "#3498db",
    "#9b59b6", "#34495e", "#e91e63", "#ff5722", "#8bc34a", "#00bcd4",
    "#673ab7", "#f48fb1", "#b39ddb", "#90a4ae"
  ];

  if (!task) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative ml-auto w-full max-w-4xl h-full bg-[#1e2126] flex flex-col md:flex-row animate-slide-up">
        {/* Mobile top bar with close */}
        <div className="flex md:hidden items-center gap-3 px-4 py-2.5 border-b border-white/10 flex-shrink-0">
          <button
            onClick={onClose}
            data-testid="task-panel-back-btn"
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-300" />
          </button>
          <span className="flex-1 text-sm text-gray-400 truncate">{title || "Task"}</span>
          <DropdownMenu open={mobileMoreMenuOpen} onOpenChange={setMobileMoreMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button data-testid="task-more-menu-mobile-btn" className="p-1.5 hover:bg-white/10 rounded-lg">
                <MoreHorizontal className="w-5 h-5 text-gray-300" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-[#2d3136] border-white/10" align="end">
              <DropdownMenuItem className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10" onClick={() => { handleMarkComplete(); setMobileMoreMenuOpen(false); }}>
                <Check className="w-5 h-5 text-green-400" />
                <span>Mark as complete</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10" onClick={() => { handleAddToMyDay(); setMobileMoreMenuOpen(false); }}>
                <Sun className="w-5 h-5 text-yellow-400" />
                <span>Add to My Day</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10" onClick={() => { handleCopyLink(); setMobileMoreMenuOpen(false); }}>
                <Link2 className="w-5 h-5 text-gray-400" />
                <span>Copy link to task</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10" onClick={() => { handleArchiveTask(); setMobileMoreMenuOpen(false); }}>
                <Archive className="w-5 h-5 text-gray-400" />
                <span>Archive</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col border-r border-white/10 min-h-0 overflow-hidden">
          {/* Header - desktop only */}
          <div className="hidden md:flex items-center gap-2 px-4 py-3 border-b border-white/10 text-sm text-gray-400 flex-shrink-0">
            <span>🏠 Mr. Chow's</span>
            <ChevronRight className="w-4 h-4" />
            <span>Ashish Innovation</span>
            <ChevronRight className="w-4 h-4" />
            <span className="truncate">Task : {boards.find(b => b.id === currentBoard)?.name.replace("Task : ", "")}</span>
            
            <div className="flex-1" />
            
            <button
              onClick={handleMarkComplete}
              data-testid="mark-complete-btn"
              className="flex items-center gap-2 px-3 py-1.5 text-gray-300 hover:bg-white/10 rounded-lg transition-colors text-sm whitespace-nowrap"
            >
              <Check className="w-4 h-4" />
              <span>Mark as complete</span>
            </button>
            
            <DropdownMenu open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button data-testid="task-more-menu-btn" className="p-1.5 hover:bg-white/10 rounded">
                  <MoreHorizontal className="w-4 h-4 text-gray-300" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-[#2d3136] border-white/10" align="end">
                <DropdownMenuItem
                  data-testid="add-to-my-day-option"
                  className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10"
                  onClick={handleAddToMyDay}
                >
                  <Sun className="w-5 h-5 text-yellow-400" />
                  <span>Add to My Day</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="copy-link-option"
                  className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10"
                  onClick={handleCopyLink}
                >
                  <Link2 className="w-5 h-5 text-gray-400" />
                  <span>Copy link</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="timesheet-option"
                  className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10"
                  onClick={() => toast.info("Timesheet coming soon")}
                >
                  <Timer className="w-5 h-5 text-gray-400" />
                  <span>Timesheet</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="custom-fields-option"
                  className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10"
                  onClick={() => toast.info("Custom fields coming soon")}
                >
                  <LayoutGrid className="w-5 h-5 text-gray-400" />
                  <span>Custom fields</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="archive-option"
                  className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10"
                  onClick={handleArchiveTask}
                >
                  <Archive className="w-5 h-5 text-gray-400" />
                  <span>Archive</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button onClick={onClose} data-testid="task-panel-close-btn" className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-gray-300" />
            </button>
          </div>

          {/* Task Content */}
          <ScrollArea className="flex-1">
            <div className="p-4 md:p-6">
              {/* Title */}
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => handleSave()}
                className="text-xl md:text-2xl font-semibold bg-transparent border-none outline-none w-full text-white placeholder:text-gray-500 mb-2"
                placeholder="Task title"
                autoFocus={!title}
                data-testid="task-title-input"
              />

              {/* Attribution badges */}
              {(task?.added_by_email || task?.last_edited_by_email) && (
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="task-attribution-badges">
                  {task?.added_by_email && task?.added_by_name && (
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30"
                      title={`Added by ${task.added_by_name} (${task.added_by_email})`}
                    >
                      Added by {task.added_by_name}
                    </span>
                  )}
                  {task?.last_edited_by_email && task?.last_edited_by_name && (
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30"
                      title={`Last edited by ${task.last_edited_by_name} (${task.last_edited_by_email})`}
                    >
                      Edited by {task.last_edited_by_name}
                    </span>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                {/* Assign - Updated to match Any.do */}
                <Popover open={assignOpen} onOpenChange={setAssignOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="assign-btn" className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-gray-300 transition-colors">
                      <Users className="w-4 h-4" />
                      Assign
                      {assignedMembers.length > 0 && (
                        <span className="ml-1 text-xs bg-blue-500 px-1.5 rounded">{assignedMembers.length}</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-96 p-0 bg-[#2d3136] border-white/10" align="start">
                    {/* Search Input */}
                    <div className="p-3 border-b border-white/10">
                      <div className="relative">
                        <Input
                          data-testid="member-search-input"
                          value={memberSearch}
                          onChange={(e) => setMemberSearch(e.target.value)}
                          placeholder="Search members by name or email"
                          className="pr-10 bg-transparent border-white/20 text-white placeholder:text-gray-500"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Members List */}
                    <ScrollArea className="max-h-80">
                      <div className="py-2">
                        {filteredMembers.length === 0 && !memberSearch && (
                          <p className="text-sm text-gray-500 text-center py-4">No team members yet. Add one below.</p>
                        )}
                        {filteredMembers.length === 0 && memberSearch && (
                          <p className="text-sm text-gray-500 text-center py-4">No members match "{memberSearch}"</p>
                        )}
                        {filteredMembers.map((member) => {
                          const isAssigned = assignees.includes(member.member_id);
                          return (
                            <div
                              key={member.member_id}
                              data-testid={`member-row-${member.member_id}`}
                              className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors"
                            >
                              {/* Avatar */}
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-white flex-shrink-0"
                                style={{ backgroundColor: member.color }}
                              >
                                {member.initials}
                              </div>
                              
                              {/* Name & Email */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{member.name}</p>
                                <p className="text-xs text-gray-500 truncate">{member.email}</p>
                              </div>
                              
                              {/* Assign/Unassign + Role */}
                              <div className="flex items-center gap-2">
                                {isAssigned ? (
                                  <button
                                    data-testid={`unassign-${member.member_id}`}
                                    onClick={() => handleToggleAssignee(member.member_id)}
                                    className="px-2 py-1 text-xs text-green-400 bg-green-400/10 rounded font-medium"
                                  >
                                    Assigned
                                  </button>
                                ) : (
                                  <button
                                    data-testid={`assign-${member.member_id}`}
                                    onClick={() => handleToggleAssignee(member.member_id)}
                                    className="px-3 py-1 text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                                  >
                                    Add
                                  </button>
                                )}
                                
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:bg-white/10 rounded transition-colors">
                                      {member.role === "admin" ? "Admin" : "Member"}
                                      <ChevronDown className="w-3 h-3" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent className="bg-[#2d3136] border-white/10">
                                    <DropdownMenuItem 
                                      className="text-white"
                                      onClick={() => handleChangeMemberRole(member.member_id, "member")}
                                    >
                                      <Check className={cn("w-4 h-4 mr-2", member.role !== "member" && "opacity-0")} />
                                      Member
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      className="text-white"
                                      onClick={() => handleChangeMemberRole(member.member_id, "admin")}
                                    >
                                      <Check className={cn("w-4 h-4 mr-2", member.role !== "admin" && "opacity-0")} />
                                      Admin
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      className="text-red-400"
                                      onClick={() => handleRemoveMember(member.member_id)}
                                    >
                                      <X className="w-4 h-4 mr-2" />
                                      Remove
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                    
                    {/* Hint to manage members */}
                    {members.length === 0 && (
                      <div className="p-3 border-t border-white/10">
                        <p className="text-xs text-gray-500 text-center">
                          {groupId
                            ? "Add members to this group first"
                            : <>Add members from <span className="text-blue-400">My Teams</span> in the sidebar</>
                          }
                        </p>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>

                {/* Due Date */}
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-gray-300 transition-colors">
                      <CalendarIcon className="w-4 h-4" />
                      {dueDate ? format(dueDate, "MMM d") : "Due date"}
                      {dueTime && <span className="text-xs text-gray-500">{dueTime}</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#2d3136] border-white/10" align="start">
                    {/* Date Tabs */}
                    <div className="flex gap-2 p-3 border-b border-white/10">
                      <button
                        onClick={() => setDateTab("start")}
                        className={cn(
                          "flex-1 px-4 py-2 text-sm rounded-lg border transition-colors",
                          dateTab === "start" ? "border-blue-500 text-blue-400" : "border-white/20 text-gray-300"
                        )}
                      >
                        Start date
                      </button>
                      <button
                        onClick={() => setDateTab("due")}
                        className={cn(
                          "flex-1 px-4 py-2 text-sm rounded-lg transition-colors",
                          dateTab === "due" ? "bg-white/10 text-white" : "text-gray-300"
                        )}
                      >
                        Due date
                      </button>
                    </div>
                    
                    <Calendar
                      mode="single"
                      selected={dateTab === "due" ? dueDate : startDate}
                      onSelect={(date) => {
                        if (dateTab === "due") setDueDate(date);
                        else setStartDate(date);
                      }}
                      className="bg-[#2d3136] text-white"
                    />
                    
                    <div className="border-t border-white/10 p-2 space-y-1">
                      {/* Time Picker */}
                      <Popover open={timeOpen} onOpenChange={setTimeOpen}>
                        <PopoverTrigger asChild>
                          <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 rounded-lg">
                            <Clock className="w-4 h-4" />
                            <span>Set due time</span>
                            {dueTime && <span className="ml-auto text-blue-400">{dueTime}</span>}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-0 bg-[#2d3136] border-white/10" align="start">
                          <ScrollArea className="h-60">
                            <div className="p-1">
                              {timeOptions.map((time) => (
                                <button
                                  key={time}
                                  onClick={() => { setDueTime(time); setTimeOpen(false); }}
                                  className={cn(
                                    "w-full px-3 py-2 text-sm text-left rounded hover:bg-white/10 transition-colors",
                                    dueTime === time ? "bg-blue-500 text-white" : "text-gray-300"
                                  )}
                                >
                                  {time}
                                </button>
                              ))}
                            </div>
                          </ScrollArea>
                        </PopoverContent>
                      </Popover>
                      
                      {/* Reminder */}
                      <Popover open={reminderOpen} onOpenChange={setReminderOpen}>
                        <PopoverTrigger asChild>
                          <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 rounded-lg">
                            <Bell className="w-4 h-4" />
                            <span>Add Reminder</span>
                            {reminders.length > 0 && (
                              <span className="ml-auto text-xs bg-blue-500 px-1.5 rounded">{reminders.length}</span>
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0 bg-[#2d3136] border-white/10" align="start">
                          <div className="p-3 border-b border-white/10">
                            <h4 className="text-sm font-medium text-white">Add Reminder</h4>
                          </div>
                          <div className="p-3 space-y-3">
                            <Calendar
                              mode="single"
                              selected={reminderDate}
                              onSelect={setReminderDate}
                              className="bg-[#2d3136] text-white"
                            />
                            <Select value={reminderTime} onValueChange={setReminderTime}>
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#2d3136] border-white/10 max-h-48">
                                {timeOptions.map((time) => (
                                  <SelectItem key={time} value={time} className="text-white">
                                    {time}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {reminders.length > 0 && (
                            <div className="px-3 pb-2 space-y-2">
                              <p className="text-xs text-gray-500">Current reminders:</p>
                              {reminders.map((rem) => (
                                <div key={rem.id} className="flex items-center justify-between text-sm text-gray-300 bg-white/5 px-2 py-1 rounded">
                                  <span>{format(new Date(rem.datetime), "MMM d, HH:mm")}</span>
                                  <button onClick={() => handleRemoveReminder(rem.id)} className="text-red-400 hover:text-red-300">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          <div className="p-3 border-t border-white/10">
                            <Button className="w-full bg-blue-500 hover:bg-blue-600" onClick={handleAddReminder}>
                              Add Reminder
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      
                      {/* Recurring */}
                      <Popover open={recurringOpen} onOpenChange={setRecurringOpen}>
                        <PopoverTrigger asChild>
                          <button className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 rounded-lg">
                            <Repeat className="w-4 h-4" />
                            <span>Recurring</span>
                            {recurringEnabled && <span className="ml-auto text-xs text-blue-400">{recurringFrequency}</span>}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0 bg-[#2d3136] border-white/10" align="start">
                          <div className="p-3 border-b border-white/10">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-medium text-white">Recurring Task</h4>
                              <Switch
                                checked={recurringEnabled}
                                onCheckedChange={setRecurringEnabled}
                              />
                            </div>
                          </div>
                          
                          {recurringEnabled && (
                            <div className="p-3 space-y-4">
                              <div className="space-y-2">
                                <label className="text-xs text-gray-400">Repeat</label>
                                <Select value={recurringFrequency} onValueChange={setRecurringFrequency}>
                                  <SelectTrigger className="bg-transparent border-white/20 text-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#2d3136] border-white/10">
                                    {recurringFrequencies.map((f) => (
                                      <SelectItem key={f.value} value={f.value} className="text-white">
                                        {f.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="space-y-2">
                                <label className="text-xs text-gray-400">Every</label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={recurringInterval}
                                    onChange={(e) => setRecurringInterval(parseInt(e.target.value) || 1)}
                                    className="w-20 bg-transparent border-white/20 text-white"
                                  />
                                  <span className="text-sm text-gray-400">
                                    {recurringFrequency === "daily" && "day(s)"}
                                    {recurringFrequency === "weekly" && "week(s)"}
                                    {recurringFrequency === "monthly" && "month(s)"}
                                    {recurringFrequency === "yearly" && "year(s)"}
                                  </span>
                                </div>
                              </div>
                              
                              {recurringFrequency === "weekly" && (
                                <div className="space-y-2">
                                  <label className="text-xs text-gray-400">On days</label>
                                  <div className="flex gap-1">
                                    {daysOfWeek.map((day, index) => (
                                      <button
                                        key={day}
                                        onClick={() => toggleRecurringDay(index)}
                                        className={cn(
                                          "w-9 h-9 rounded-lg text-xs font-medium transition-colors",
                                          recurringDays.includes(index)
                                            ? "bg-blue-500 text-white"
                                            : "bg-white/10 text-gray-400 hover:bg-white/20"
                                        )}
                                      >
                                        {day}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="p-3 border-t border-white/10">
                            <Button
                              className="w-full bg-blue-500 hover:bg-blue-600"
                              onClick={() => { setRecurringOpen(false); handleSave(true); }}
                            >
                              Save
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    {dueDate && (
                      <div className="p-2 border-t border-white/10">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-gray-400"
                          onClick={() => { setDueDate(null); setDueTime(""); }}
                        >
                          Clear date
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>

                {/* Section/Board */}
                <Popover open={sectionOpen} onOpenChange={setSectionOpen}>
                  <PopoverTrigger asChild>
                    <button className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-gray-300 transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Task
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0 bg-[#2d3136] border-white/10" align="start">
                    <div className="p-3 border-b border-white/10">
                      <h3 className="text-sm font-medium text-white text-center">Section</h3>
                    </div>
                    <div className="py-2">
                      {boards.map((board) => (
                        <button
                          key={board.id}
                          onClick={() => handleBoardChange(board.id)}
                          className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          <span>{board.name}</span>
                          {currentBoard === board.id && (
                            <Check className="w-4 h-4 text-blue-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {selectedTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-3 py-1 rounded-full text-sm text-white"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </span>
                ))}
                
                {/* Tags Popover */}
                <Popover open={tagsOpen} onOpenChange={setTagsOpen}>
                  <PopoverTrigger asChild>
                    <button className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                      <Plus className="w-4 h-4 text-gray-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0 bg-[#2d3136] border-white/10" align="start">
                    <div className="flex items-center justify-between p-3 border-b border-white/10">
                      <span className="text-sm font-medium text-gray-400">TAGS</span>
                      <button
                        onClick={() => { setTagsOpen(false); setNewTagOpen(true); }}
                        className="p-1 hover:bg-white/10 rounded"
                      >
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    <div className="p-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={tagSearch}
                          onChange={(e) => setTagSearch(e.target.value)}
                          placeholder="Search tags"
                          className="pl-10 bg-transparent border-white/20 text-white"
                        />
                      </div>
                    </div>
                    <div className="px-2 pb-2 max-h-60 overflow-y-auto">
                      {filteredTags.map((tag) => {
                        const isCustom = tag.id.startsWith("tag_");
                        return (
                          <div
                            key={tag.id}
                            className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-white text-sm mb-1 group/tag"
                            style={{ backgroundColor: tag.color }}
                          >
                            <button
                              onClick={() => toggleTag(tag)}
                              className="flex items-center gap-3 flex-1"
                            >
                              <div className={cn(
                                "w-5 h-5 rounded-full border-2 border-white/50 flex items-center justify-center",
                                selectedTags.find(t => t.id === tag.id) && "bg-white"
                              )}>
                                {selectedTags.find(t => t.id === tag.id) && (
                                  <Check className="w-3 h-3" style={{ color: tag.color }} />
                                )}
                              </div>
                              <span>{tag.name}</span>
                            </button>
                            {isCustom ? (
                              <button
                                onClick={() => handleDeleteTag(tag.id)}
                                className="opacity-0 group-hover/tag:opacity-100 p-1 hover:bg-white/20 rounded transition-opacity"
                                data-testid={`delete-tag-${tag.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <Edit2 className="w-4 h-4 opacity-50" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 p-3 border-t border-white/10">
                      <Button variant="ghost" className="flex-1 text-white" onClick={() => setTagsOpen(false)}>Cancel</Button>
                      <Button className="flex-1 bg-blue-500 hover:bg-blue-600" onClick={() => setTagsOpen(false)}>Save</Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* New Tag Popover */}
                <Popover open={newTagOpen} onOpenChange={setNewTagOpen}>
                  <PopoverTrigger asChild><span /></PopoverTrigger>
                  <PopoverContent className="w-72 p-0 bg-[#2d3136] border-white/10" align="start">
                    <div className="flex items-center gap-2 p-3 border-b border-white/10">
                      <button onClick={() => { setNewTagOpen(false); setTagsOpen(true); }} className="p-1 hover:bg-white/10 rounded">
                        <ChevronRight className="w-4 h-4 text-gray-400 rotate-180" />
                      </button>
                      <span className="text-sm font-medium text-gray-400">NEW TAG</span>
                    </div>
                    <div className="p-4 space-y-4">
                      <Input
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        placeholder="Tag name"
                        className="bg-transparent border-white/20 text-white"
                      />
                      <div className="flex flex-wrap gap-2">
                        {tagColors.map((color) => (
                          <button
                            key={color}
                            onClick={() => setNewTagColor(color)}
                            className={cn(
                              "w-8 h-8 rounded-full transition-transform",
                              newTagColor === color && "ring-2 ring-offset-2 ring-offset-[#2d3136] ring-white scale-110"
                            )}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="p-3 border-t border-white/10">
                      <Button
                        className="w-full bg-blue-500 hover:bg-blue-600"
                        data-testid="save-new-tag-btn"
                        onClick={handleCreateTag}
                      >
                        Save
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Notes */}
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => handleSave()}
                placeholder="Add notes..."
                className="min-h-[120px] bg-transparent border-none text-gray-300 placeholder:text-gray-600 resize-none"
              />

              {/* Checklist Section */}
              {checklist.length > 0 && (
                <div className="mt-6" data-testid="checklist-section">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckSquare className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-gray-300">Checklist</span>
                    <span className="text-xs text-gray-500">
                      {checklist.filter(c => c.completed).length}/{checklist.length}
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-1.5 bg-white/10 rounded-full mb-3 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-300"
                      style={{ width: `${checklist.length > 0 ? (checklist.filter(c => c.completed).length / checklist.length) * 100 : 0}%` }}
                    />
                  </div>
                  <div className="space-y-1">
                    {checklist.map((item) => (
                      <div
                        key={item.id}
                        data-testid={`checklist-item-${item.id}`}
                        className="flex items-center gap-3 group px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <button
                          data-testid={`checklist-toggle-${item.id}`}
                          onClick={() => handleToggleChecklistItem(item.id)}
                          className={cn(
                            "w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                            item.completed
                              ? "bg-blue-500 border-blue-500"
                              : "border-gray-500 hover:border-blue-400"
                          )}
                        >
                          {item.completed && <Check className="w-3 h-3 text-white" />}
                        </button>
                        <span className={cn(
                          "flex-1 text-sm",
                          item.completed ? "text-gray-500 line-through" : "text-gray-200"
                        )}>
                          {item.text}
                        </span>
                        <button
                          data-testid={`checklist-delete-${item.id}`}
                          onClick={() => handleDeleteChecklistItem(item.id)}
                          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
                        >
                          <X className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {/* Add item inline */}
                  <div className="flex items-center gap-2 mt-2 px-2">
                    <button
                      data-testid="checklist-add-inline-btn"
                      onClick={() => setShowChecklistInput(true)}
                      className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-400 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add item
                    </button>
                  </div>
                  {showChecklistInput && (
                    <div className="flex items-center gap-2 mt-2 px-2">
                      <Input
                        data-testid="checklist-new-item-input"
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        placeholder="Add an item..."
                        className="flex-1 bg-white/5 border-white/20 text-white text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddChecklistItem();
                          if (e.key === "Escape") { setShowChecklistInput(false); setNewChecklistItem(""); }
                        }}
                      />
                      <Button
                        data-testid="checklist-save-item-btn"
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                        onClick={handleAddChecklistItem}
                      >
                        Add
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-400"
                        onClick={() => { setShowChecklistInput(false); setNewChecklistItem(""); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Attachments Section */}
              {attachments.length > 0 && (
                <div className="mt-6" data-testid="attachments-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Paperclip className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-gray-300">Attachments</span>
                    <span className="text-xs text-gray-500">{attachments.length}</span>
                  </div>
                  <div className="space-y-2">
                    {attachments.map((att) => {
                      const FileIcon = getFileIcon(att.content_type);
                      return (
                        <div
                          key={att.id}
                          data-testid={`attachment-${att.id}`}
                          className="flex items-center gap-3 px-3 py-2.5 bg-white/5 rounded-lg group hover:bg-white/10 transition-colors"
                        >
                          <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                            <FileIcon className="w-4 h-4 text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-200 truncate">{att.filename}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(att.size)}</p>
                          </div>
                          <button
                            data-testid={`attachment-download-${att.id}`}
                            onClick={() => handleDownloadAttachment(att)}
                            className="p-1.5 hover:bg-white/10 rounded transition-colors"
                          >
                            <Download className="w-4 h-4 text-gray-400" />
                          </button>
                          <button
                            data-testid={`attachment-delete-${att.id}`}
                            onClick={() => handleDeleteAttachment(att.id)}
                            className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                data-testid="file-upload-input"
              />

              {/* Blue + FAB Button */}
              <div className="mt-6 flex justify-end">
                <DropdownMenu open={plusMenuOpen} onOpenChange={setPlusMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="task-add-btn"
                      className="w-10 h-10 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30 transition-all hover:scale-105"
                    >
                      <Plus className="w-5 h-5 text-white" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-48 bg-[#2d3136] border-white/10" align="end">
                    <DropdownMenuItem
                      data-testid="add-attachment-option"
                      className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10"
                      onClick={() => {
                        setPlusMenuOpen(false);
                        fileInputRef.current?.click();
                      }}
                    >
                      <Paperclip className="w-5 h-5 text-blue-400" />
                      <span>Attachment</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="add-checklist-option"
                      className="flex items-center gap-3 px-4 py-3 text-gray-200 cursor-pointer hover:bg-white/10"
                      onClick={() => {
                        setPlusMenuOpen(false);
                        setShowChecklistInput(true);
                      }}
                    >
                      <CheckSquare className="w-5 h-5 text-green-400" />
                      <span>Checklist</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Checklist input when no items yet */}
              {checklist.length === 0 && showChecklistInput && (
                <div className="mt-4" data-testid="checklist-empty-input">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckSquare className="w-4 h-4 text-blue-400" />
                    <span className="text-sm font-medium text-gray-300">Checklist</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="checklist-first-item-input"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      placeholder="Add an item..."
                      className="flex-1 bg-white/5 border-white/20 text-white text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddChecklistItem();
                        if (e.key === "Escape") { setShowChecklistInput(false); setNewChecklistItem(""); }
                      }}
                    />
                    <Button
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 text-white"
                      onClick={handleAddChecklistItem}
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400"
                      onClick={() => { setShowChecklistInput(false); setNewChecklistItem(""); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-4 flex items-center gap-3 px-3 py-2.5 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-blue-300">Uploading file...</span>
                </div>
              )}

              {/* Mobile Comments Section */}
              <div className="md:hidden mt-6 border-t border-white/10 pt-4" data-testid="mobile-comments-section">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</span>
                <div className="space-y-3 mt-3">
                  {comments.map((c) => {
                    const isSelf = c.user_id === currentUser?.user_id;
                    const initials = c.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U';
                    return (
                      <div key={c.id} className="flex gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium text-white flex-shrink-0 ${isSelf ? 'bg-emerald-600' : 'bg-blue-600'}`}>
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-medium ${isSelf ? 'text-emerald-400' : 'text-blue-400'}`}>{c.user_name || 'User'}</p>
                          <p className="text-sm text-gray-200 mt-0.5 break-words">{c.text}</p>
                          <p className="text-[10px] text-gray-600 mt-0.5">
                            {c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : 'Just now'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {comments.length === 0 && (
                    <p className="text-xs text-gray-600 text-center py-2">No comments yet</p>
                  )}
                </div>
              </div>
            </div>
          </ScrollArea>

          {/* Mobile Comment Input - sticky at bottom */}
          <div className="md:hidden flex items-center gap-2 px-3 py-2 border-t border-white/10 bg-[#1e2126] flex-shrink-0" data-testid="mobile-comment-input">
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 bg-white/5 border-white/15 text-white text-sm h-9"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
            />
            <button
              onClick={handleAddComment}
              data-testid="mobile-send-comment-btn"
              className="p-2 text-blue-400 hover:bg-white/10 rounded-lg flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Chat & Activity Panel */}
        <div className="hidden md:flex w-80 flex-col bg-[#1e2126]">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-400">CHAT & ACTIVITY</span>
            <button className="p-1 hover:bg-white/10 rounded">
              <Zap className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          {/* Activity/Comments Feed */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {/* System messages */}
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-medium text-white flex-shrink-0">
                  {currentUser?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                </div>
                <div>
                  <p className="text-sm text-gray-400"><span className="text-emerald-400 font-medium">{currentUser?.name || 'You'}</span> added this task</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {task?.created_at ? formatDistanceToNow(new Date(task.created_at), { addSuffix: true }) : 'recently'}
                  </p>
                </div>
              </div>

              {/* Comments */}
              {comments.map((c) => {
                const isSelf = c.user_id === currentUser?.user_id;
                const initials = c.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U';
                return (
                  <div key={c.id} className="group">
                    <div className="flex gap-3">
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0 ${
                          isSelf ? 'bg-emerald-600' : 'bg-blue-600'
                        }`}
                      >
                        {initials}
                      </div>
                      <div className="flex-1">
                        <p className={`text-xs font-medium mb-1 ${isSelf ? 'text-emerald-400' : 'text-blue-400'}`}>
                          {c.user_name || 'User'}
                        </p>
                        <div className="flex items-start justify-between">
                          <div className={`px-3 py-2 rounded-lg text-white text-sm max-w-[200px] ${
                            isSelf ? 'bg-emerald-600' : 'bg-blue-600'
                          }`}>
                            {c.text}
                          </div>
                          <button
                            onClick={() => handleDeleteComment(c.id)}
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
                          >
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : 'Just now'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {comments.length === 0 && (
                <p className="text-sm text-gray-600 text-center py-4">No comments yet</p>
              )}
            </div>
          </ScrollArea>

          {/* Comment Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-xs font-medium text-white">
                {currentUser?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
              </div>
              <Input
                ref={commentInputRef}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 bg-transparent border-none text-white placeholder:text-gray-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddComment();
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button className="p-2 hover:bg-white/10 rounded-lg">
                  <AtSign className="w-4 h-4 text-gray-400" />
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-2 hover:bg-white/10 rounded-lg"
                    data-testid="emoji-picker-btn"
                  >
                    <Smile className="w-4 h-4 text-gray-400" />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-10 left-0 z-50" data-testid="emoji-picker">
                      <EmojiPicker
                        theme={Theme.DARK}
                        width={320}
                        height={400}
                        searchPlaceholder="Search"
                        onEmojiClick={(emojiData) => {
                          setComment(prev => prev + emojiData.emoji);
                          commentInputRef.current?.focus();
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <button 
                onClick={handleAddComment}
                className="p-2 hover:bg-white/10 rounded-lg text-blue-400"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
